import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import { Tooltip as ReactTippy } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import CurrencyHelper from '../../../helpers/currencyHelper';
import styles from './TopItemsWidget.module.css';
import { getInitials } from '../../../utils/string/nameUtils';
import { formatTimeElapsedOrDate } from '../../../utils/time/formatTimeElapsed';
import SystemOnlyBadge from '../../../components/common/SystemOnlyBadge';

interface TopItemsWidgetProps {
  title: string;
  /** Marks a widget only system-level credentials get; the badge follows the title. */
  systemOnly?: boolean;
  items: any[];
  isLoading?: boolean;
  type: 'campaign' | 'organization' | 'event' | 'application';
  viewAllLink?: string;
}

interface TopItemData {
  id: number;
  name: string;
  amount: number;
  currency: string;
  mainImageUrl?: string;
  subtitle?: string;
  timestamp?: string;
}

// Helper component for name with tooltip
const ItemNameWithTooltip: React.FC<{ name: string }> = ({ name }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = nameRef.current;
      if (element) {
        // Check if text is truncated
        const overflow = element.scrollWidth > element.clientWidth;
        setIsOverflowing(overflow);
      }
    };

    checkOverflow();

    // Recheck on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [name]);

  return (
    // @ts-ignore: ReactTippy typing issues
    <ReactTippy
      title={name}
      disabled={!isOverflowing}
      position="top"
      arrow={true}
      theme="dark"
      size="small"
      distance={8}
      hideOnClick={true}
      interactive={false}
      animation="fade"
      duration={200}
    >
      <span
        ref={nameRef}
        className={styles['topItemNameText']}
        tabIndex={isOverflowing ? 0 : -1}
        role={isOverflowing ? 'button' : 'text'}
        aria-label={isOverflowing ? `Full name: ${name}` : undefined}
      >
        {name}
      </span>
    </ReactTippy>
  );
};

export const TopItemsWidget: React.FC<TopItemsWidgetProps> = ({
  title,
  systemOnly,
  items,
  isLoading = false,
  type,
  viewAllLink
}) => {
  // Transform API data to common format
  const transformItem = (item: any): TopItemData => {
    if (type === 'application') {
      // For charity place applications (EventAmbassador)
      const displayName = `${item.firstName} ${item.lastName}`;
      const event = item.event || {};
      const createdAt = `${item.createdAt}Z`; //this is a hack to fix the timezone issue
      const targetAmount = item.targetAmount || 0;
      const currency = item.organization.currency;
      const utcTimeNow = new Date().toISOString();

      return {
        id: item.id,
        name: displayName,
        subtitle: event.name || '',
        timestamp: createdAt
          ? formatTimeElapsedOrDate(createdAt, utcTimeNow, { dateFormatOptions: { includeTime: false } })
          : '',
        amount: targetAmount || 0,
        currency: currency,
        mainImageUrl: event.mainImageUrl
      };
    }

    const entityData = item.campaign || item.event || item.organization;
    return {
      id:
        entityData?.id ||
        item.campaignId ||
        item.eventId ||
        item.organizationId,
      name: entityData?.name || 'Unknown',
      amount: item.raisedAmount || 0,
      currency: entityData?.currency || 'aud',
      mainImageUrl: entityData?.mainImageUrl
    };
  };

  const transformedItems = items.map(transformItem);

  const getItemUrl = (item: TopItemData): string => {
    switch (type) {
      case 'campaign':
        return `/campaigns/performance/${item.id}`;
      case 'event':
        return `/events/performance/${item.id}`;
      case 'organization':
        return `/organizations/dashboard/${item.id}`;
      case 'application':
        return `/vip-program/vip/${item.id}/part-one`;
      default:
        return '#';
    }
  };

  const getImageUrl = (item: TopItemData) => {
    if (!item.mainImageUrl) return null;
    return `${item.mainImageUrl}?w=200`;
  };

  const renderItemImage = (item: TopItemData) => {
    const imageUrl = getImageUrl(item);

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          className={styles['topItemImage']}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            // Hide image on error
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
          alt={item.name}
        />
      );
    }

    // Fallback to colored circle with first letter
    const initials = getInitials(item.name);
    return (
      <div
        className={`${styles['topItemAvatar']} ${styles[`topItemAvatar${type.charAt(0).toUpperCase() + type.slice(1)}`]}`}
      >
        {initials}
      </div>
    );
  };

  const renderItem = (item: TopItemData, index: number) => {
    // Format currency without decimals by rounding first
    const roundedAmount = Math.round(item.amount);
    const formattedAmount = CurrencyHelper.formatCurrency(
      roundedAmount,
      item.currency,
      {
        notation: 'compact',
      }
    );
    const itemUrl = getItemUrl(item);

    return (
      <Link to={itemUrl} key={item.id} className={styles['topItem']}>
        {type !== 'application' && (
          <div className={styles['topItemRank']}>
            {index + 1}
          </div>
        )}
        <div className={styles['topItemImageContainer']}>
          {renderItemImage(item)}
        </div>
        <div className={styles['topItemContent']}>
          <div className={styles['topItemName']}>
            {item.name}
          </div>
          {item.subtitle && (
            <div className={styles['topItemSubtitle']}>{item.subtitle}</div>
          )}
          {item.timestamp && (
            <div className={styles['topItemTimestamp']}>
              {item.timestamp}
            </div>
          )}
        </div>
        <div className={styles['topItemAmount']}>
          {type === 'application' && (
            <span style={{ fontWeight: 700 }}>Goal: </span>
          )}
          {formattedAmount}
        </div>
      </Link>
    );
  };

  const renderLoadingItem = (index: number) => (
    <div
      key={`loading-${index}`}
      className={`${styles['topItem']} ${styles['topItemLoading']}`}
    >
      {type !== 'application' && (
        <div className={styles['topItemRank']}>
          {index + 1}
        </div>
      )}
      <div className={styles['topItemImageContainer']}>
        <div className={styles['topItemSkeletonImage']} />
      </div>
      <div className={styles['topItemContent']}>
        <div className={styles['topItemSkeletonName']} />
        {type === 'application' && (
          <>
            <div className={styles['topItemSkeletonSubtitle']} />
            <div className={styles['topItemSkeletonTimestamp']} />
          </>
        )}
      </div>
      <div className={styles['topItemSkeletonAmount']} />
    </div>
  );

  return (
    <div className={styles['topItemsWidgetContainer']}>
      <div className={styles['topItemsCard']}>
        <div className={styles['topItemsCardContent']}>
          <div className={styles['topItemsTitle']}>
            {title}
            {systemOnly && <SystemOnlyBadge />}
          </div>

          {isLoading ? (
            <div className={styles['topItemsLoading']}>
              <div className={styles['topItemsList']}>
                {[...Array(5)].map((_, index) => renderLoadingItem(index))}
              </div>
            </div>
          ) : transformedItems.length > 0 ? (
            <>
              <div className={styles['topItemsList']}>
                {transformedItems.map((item, index) => renderItem(item, index))}
              </div>
              {viewAllLink && (
                <div className={styles['viewAllContainer']}>
                  <Link to={viewAllLink} className={styles['viewAllLink']}>
                    View all →
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className={styles['topItemsEmpty']}>
              <p>{I18n.t('home.topItems.noData')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
